/**
 * EmployeeFieldIncidentForm — filed by a Supervisor / Crew Leader when
 * an employee is involved in a field incident (injury, near-miss,
 * altercation, property damage, vehicle-related).
 *
 * Posts to /api/employee-field-incidents which auto-routes to HR and
 * Safety & Risk Management via dept_tasks. Insurance handling and OSHA
 * follow-up are triggered from the resulting task.
 */
import { useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { AlertTriangle, ShieldAlert, Camera, X, User as UserIcon } from 'lucide-react';
import { toast } from 'sonner';
import { US_STATES } from '../../lib/usStates';

const API = process.env.REACT_APP_BACKEND_URL;

const INCIDENT_TYPES = [
  { key: 'injury', label: 'Injury' },
  { key: 'near_miss', label: 'Near miss' },
  { key: 'altercation', label: 'Altercation / conflict' },
  { key: 'property_damage', label: 'Property damage' },
  { key: 'vehicle_related', label: 'Vehicle related' },
  { key: 'exposure', label: 'Hazardous exposure' },
  { key: 'other', label: 'Other' },
];

const BODY_PARTS = [
  'Head', 'Neck', 'Back', 'Shoulder', 'Arm', 'Hand', 'Finger',
  'Chest', 'Abdomen', 'Hip', 'Leg', 'Knee', 'Ankle', 'Foot', 'Eye',
];

export default function EmployeeFieldIncidentForm({ regions = [], properties = [] }) {
  const [form, setForm] = useState({
    employee_id: '',
    employee_name: '',
    employee_role: '',
    employee_badge: '',
    incident_datetime: new Date().toISOString().slice(0, 16),
    state: '',
    region_id: '',
    city: '',
    property_id: '',
    address_line: '',
    incident_type: '',
    description: '',
    witnesses: [],
    injury_involved: false,
    injury_body_parts: [],
    medical_attention_required: false,
    hospital_or_clinic: '',
    work_missed: false,
    days_missed_estimate: '',
    insurance_notification_required: false,
    supervisor_recommendation: '',
    immediate_actions_taken: '',
    photos: [],
  });
  const [saving, setSaving] = useState(false);

  const filteredRegions = regions.filter(
    (r) => !form.state || (r.state || 'TX').toUpperCase() === form.state,
  );

  const selectedRegion = regions.find((r) => r.region_id === form.region_id);
  const cityOptions = selectedRegion?.cities || [];

  const addWitness = () =>
    setForm((f) => ({ ...f, witnesses: [...f.witnesses, { name: '', role: '', contact: '' }] }));
  const removeWitness = (i) =>
    setForm((f) => ({ ...f, witnesses: f.witnesses.filter((_, idx) => idx !== i) }));
  const updateWitness = (i, key, val) =>
    setForm((f) => ({
      ...f,
      witnesses: f.witnesses.map((w, idx) => (idx === i ? { ...w, [key]: val } : w)),
    }));

  const toggleBodyPart = (part) =>
    setForm((f) => ({
      ...f,
      injury_body_parts: f.injury_body_parts.includes(part)
        ? f.injury_body_parts.filter((p) => p !== part)
        : [...f.injury_body_parts, part],
    }));

  const addPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setForm((f) => ({ ...f, photos: [...f.photos, { data: reader.result, name: file.name }] }));
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const removePhoto = (idx) =>
    setForm((f) => ({ ...f, photos: f.photos.filter((_, i) => i !== idx) }));

  const submit = async () => {
    if (!form.employee_name.trim()) return toast.error('Employee name is required');
    if (!form.incident_type) return toast.error('Pick an incident type');
    if (!form.description.trim() || form.description.trim().length < 20)
      return toast.error('Description must be at least 20 characters — this record supports insurance claims');

    setSaving(true);
    try {
      const payload = {
        ...form,
        region_code: selectedRegion?.region_code,
        property_name: (properties.find((p) => p.property_id === form.property_id) || {}).property_name,
        days_missed_estimate: form.days_missed_estimate ? Number(form.days_missed_estimate) : null,
      };
      const res = await fetch(`${API}/api/employee-field-incidents`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) return toast.error(data.detail || 'Submit failed');
      toast.success('Field incident logged — HR + Safety & Risk notified');
      setForm({
        employee_id: '', employee_name: '', employee_role: '', employee_badge: '',
        incident_datetime: new Date().toISOString().slice(0, 16),
        state: '', region_id: '', city: '', property_id: '', address_line: '',
        incident_type: '', description: '', witnesses: [],
        injury_involved: false, injury_body_parts: [], medical_attention_required: false,
        hospital_or_clinic: '', work_missed: false, days_missed_estimate: '',
        insurance_notification_required: false,
        supervisor_recommendation: '', immediate_actions_taken: '', photos: [],
      });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      className="bg-zinc-900 border-2 border-red-600/40 p-5"
      data-testid="employee-field-incident-form"
    >
      <div className="flex items-center gap-2 mb-4">
        <ShieldAlert className="h-5 w-5 text-red-500" />
        <h3 className="text-white font-bold">Employee Field Incident Report</h3>
        <Badge className="bg-red-700 text-white text-[10px]">HR + Safety &amp; Risk</Badge>
      </div>
      <p className="text-[11px] text-zinc-400 mb-4">
        Professional-grade record used for company logs, OSHA compliance, and
        insurance-carrier handling. Complete every applicable section.
      </p>

      {/* Employee block */}
      <SectionHeader icon={UserIcon} label="Employee involved" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <Field label="Full name *">
          <Input
            value={form.employee_name}
            onChange={(e) => setForm({ ...form, employee_name: e.target.value })}
            className="bg-zinc-950 border-zinc-700 text-white"
            data-testid="efi-employee-name"
          />
        </Field>
        <Field label="Role / Position">
          <Input
            value={form.employee_role}
            onChange={(e) => setForm({ ...form, employee_role: e.target.value })}
            className="bg-zinc-950 border-zinc-700 text-white"
            data-testid="efi-employee-role"
          />
        </Field>
        <Field label="Badge #">
          <Input
            value={form.employee_badge}
            onChange={(e) => setForm({ ...form, employee_badge: e.target.value })}
            className="bg-zinc-950 border-zinc-700 text-white font-mono"
            data-testid="efi-employee-badge"
          />
        </Field>
        <Field label="NECS user ID (if known)">
          <Input
            value={form.employee_id}
            onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
            className="bg-zinc-950 border-zinc-700 text-white font-mono"
            data-testid="efi-employee-id"
          />
        </Field>
      </div>

      {/* When / Where */}
      <SectionHeader icon={AlertTriangle} label="When and where" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <Field label="Date & time *">
          <Input
            type="datetime-local"
            value={form.incident_datetime}
            onChange={(e) => setForm({ ...form, incident_datetime: e.target.value })}
            className="bg-zinc-950 border-zinc-700 text-white"
            data-testid="efi-datetime"
          />
        </Field>
        <Field label="State">
          <Select value={form.state} onValueChange={(v) => setForm({ ...form, state: v, region_id: '', city: '' })}>
            <SelectTrigger className="bg-zinc-950 border-zinc-700 text-white" data-testid="efi-state">
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700 text-white max-h-72">
              {US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Region">
          <Select value={form.region_id} onValueChange={(v) => setForm({ ...form, region_id: v, city: '' })}>
            <SelectTrigger className="bg-zinc-950 border-zinc-700 text-white" data-testid="efi-region">
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700 text-white max-h-72">
              {filteredRegions.map((r) => (
                <SelectItem key={r.region_id} value={r.region_id}>{r.region_code} — {r.state}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="City">
          <Select value={form.city} onValueChange={(v) => setForm({ ...form, city: v })}>
            <SelectTrigger className="bg-zinc-950 border-zinc-700 text-white" data-testid="efi-city">
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700 text-white max-h-72">
              {cityOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Property">
          <Select value={form.property_id} onValueChange={(v) => setForm({ ...form, property_id: v })}>
            <SelectTrigger className="bg-zinc-950 border-zinc-700 text-white" data-testid="efi-property">
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700 text-white max-h-72">
              {properties.map((p) => (
                <SelectItem key={p.property_id} value={p.property_id}>{p.property_name || p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Address (optional)" full>
          <Input
            value={form.address_line}
            onChange={(e) => setForm({ ...form, address_line: e.target.value })}
            className="bg-zinc-950 border-zinc-700 text-white"
            data-testid="efi-address"
          />
        </Field>
      </div>

      {/* What happened */}
      <SectionHeader icon={AlertTriangle} label="What happened" />
      <div className="space-y-3 mb-4">
        <Field label="Incident type *">
          <Select value={form.incident_type} onValueChange={(v) => setForm({ ...form, incident_type: v })}>
            <SelectTrigger className="bg-zinc-950 border-zinc-700 text-white" data-testid="efi-incident-type">
              <SelectValue placeholder="Choose…" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700 text-white">
              {INCIDENT_TYPES.map((t) => (
                <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Description of events *">
          <Textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="bg-zinc-950 border-zinc-700 text-white min-h-[120px]"
            placeholder="Time-boxed narrative of what happened, in the employee's own words if possible."
            data-testid="efi-description"
          />
          <p className="text-[10px] text-zinc-500 mt-1">Minimum 20 characters — this record may be shared with an insurance carrier.</p>
        </Field>

        {/* Witnesses */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <Label className="text-zinc-300 text-xs">Witnesses ({form.witnesses.length})</Label>
            <Button
              type="button" variant="outline" size="sm"
              className="h-7 text-xs border-zinc-700 text-zinc-300"
              onClick={addWitness} data-testid="efi-add-witness"
            >
              + Add witness
            </Button>
          </div>
          {form.witnesses.map((w, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2" data-testid={`efi-witness-row-${i}`}>
              <Input placeholder="Name" value={w.name} onChange={(e) => updateWitness(i, 'name', e.target.value)}
                className="bg-zinc-950 border-zinc-700 text-white" />
              <Input placeholder="Role / relation" value={w.role} onChange={(e) => updateWitness(i, 'role', e.target.value)}
                className="bg-zinc-950 border-zinc-700 text-white" />
              <div className="flex gap-1">
                <Input placeholder="Phone / email" value={w.contact} onChange={(e) => updateWitness(i, 'contact', e.target.value)}
                  className="bg-zinc-950 border-zinc-700 text-white" />
                <Button type="button" variant="outline" size="icon"
                  className="border-red-500 text-red-400"
                  onClick={() => removeWitness(i)}>
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Injury */}
      <SectionHeader icon={AlertTriangle} label="Injury & medical" />
      <div className="space-y-3 mb-4">
        <ToggleRow
          checked={form.injury_involved}
          onChange={(v) => setForm({ ...form, injury_involved: v })}
          label="Injury involved?"
          testid="efi-injury-toggle"
        />
        {form.injury_involved && (
          <>
            <Field label="Body parts affected">
              <div className="flex flex-wrap gap-1.5">
                {BODY_PARTS.map((bp) => {
                  const active = form.injury_body_parts.includes(bp);
                  return (
                    <button
                      key={bp} type="button" onClick={() => toggleBodyPart(bp)}
                      className={`text-[11px] px-2 py-1 rounded border ${
                        active
                          ? 'bg-red-700 text-white border-red-500'
                          : 'bg-zinc-950 text-zinc-300 border-zinc-700 hover:border-red-500'
                      }`}
                      data-testid={`efi-bodypart-${bp.toLowerCase()}`}
                    >
                      {bp}
                    </button>
                  );
                })}
              </div>
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <ToggleRow
                checked={form.medical_attention_required}
                onChange={(v) => setForm({ ...form, medical_attention_required: v })}
                label="Medical attention"
                testid="efi-medical-toggle"
              />
              <Field label="Hospital / Clinic">
                <Input
                  value={form.hospital_or_clinic}
                  onChange={(e) => setForm({ ...form, hospital_or_clinic: e.target.value })}
                  className="bg-zinc-950 border-zinc-700 text-white"
                  data-testid="efi-hospital"
                />
              </Field>
              <Field label="Days missed (est.)">
                <Input
                  type="number" min="0"
                  value={form.days_missed_estimate}
                  onChange={(e) => setForm({ ...form, days_missed_estimate: e.target.value })}
                  className="bg-zinc-950 border-zinc-700 text-white"
                  data-testid="efi-days-missed"
                />
              </Field>
            </div>
            <ToggleRow
              checked={form.work_missed}
              onChange={(v) => setForm({ ...form, work_missed: v })}
              label="Work missed (any duration)"
              testid="efi-work-missed"
            />
          </>
        )}
      </div>

      {/* Follow-up */}
      <SectionHeader icon={ShieldAlert} label="Follow-up" />
      <div className="space-y-3 mb-4">
        <ToggleRow
          checked={form.insurance_notification_required}
          onChange={(v) => setForm({ ...form, insurance_notification_required: v })}
          label="Notify insurance carrier"
          testid="efi-insurance-toggle"
        />
        <Field label="Immediate actions taken on scene">
          <Textarea
            value={form.immediate_actions_taken}
            onChange={(e) => setForm({ ...form, immediate_actions_taken: e.target.value })}
            className="bg-zinc-950 border-zinc-700 text-white min-h-[70px]"
            data-testid="efi-actions"
          />
        </Field>
        <Field label="Supervisor recommendation">
          <Textarea
            value={form.supervisor_recommendation}
            onChange={(e) => setForm({ ...form, supervisor_recommendation: e.target.value })}
            className="bg-zinc-950 border-zinc-700 text-white min-h-[70px]"
            data-testid="efi-recommendation"
          />
        </Field>
      </div>

      {/* Photos */}
      <SectionHeader icon={Camera} label={`Photo evidence (${form.photos.length})`} />
      <div className="flex flex-wrap gap-2 mb-4">
        {form.photos.map((p, i) => (
          <div key={i} className="relative">
            <img src={p.data} alt={p.name} className="w-24 h-24 object-cover rounded border border-zinc-700" />
            <button type="button" onClick={() => removePhoto(i)}
              className="absolute -top-1 -right-1 bg-red-600 text-white rounded-full p-0.5">
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
        <label
          htmlFor="efi-photo-input"
          className="w-24 h-24 flex flex-col items-center justify-center rounded border border-dashed border-zinc-600 text-zinc-400 cursor-pointer hover:border-red-500"
          data-testid="efi-photo-add"
        >
          <Camera className="h-5 w-5" />
          <span className="text-[10px] mt-1">Add photo</span>
        </label>
        <input id="efi-photo-input" type="file" accept="image/*" capture="environment" onChange={addPhoto} className="hidden" />
      </div>

      <Button
        onClick={submit} disabled={saving}
        className="w-full font-bold bg-red-600 hover:bg-red-700 text-white"
        data-testid="efi-submit"
      >
        {saving ? 'Submitting…' : 'Submit — Route to HR + Safety & Risk'}
      </Button>
    </Card>
  );
}

function SectionHeader({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-1.5 mt-3 mb-2 pb-1 border-b border-zinc-800">
      <Icon className="h-3.5 w-3.5 text-red-400" />
      <span className="text-[10px] uppercase tracking-widest text-red-300 font-bold">{label}</span>
    </div>
  );
}
function Field({ label, children, full = false }) {
  return (
    <div className={full ? 'sm:col-span-2 lg:col-span-4' : ''}>
      <Label className="text-zinc-300 text-xs">{label}</Label>
      {children}
    </div>
  );
}
function ToggleRow({ checked, onChange, label, testid }) {
  return (
    <div className="flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded px-3 py-2">
      <span className="text-xs text-zinc-300">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}
